import React, { useState, useMemo, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import {
  FaStar,
  FaCheckCircle,
  FaMapMarkerAlt,
  FaSearch,
  FaChevronDown,
  FaLeaf,
  FaSeedling,
  FaBug,
  FaAppleAlt,
  FaBox,
  FaStore,
  FaFilter,
} from "react-icons/fa";
import ProductCard from "../../../../components/customer/cards/productCard/ProductCard";
import Footer from "../../../../components/general/footer/Footer";
import MyNavbar from "../../../../components/general/navbar/MyNavbar";
import { SingleProductSkeleton } from "../../../../components/general/skeleton/SkeletonBlock";
import "../../../../assets/css/customer/shop/shopdetails/ShopDetails.css";
import MyContext from "../../../../context/MyContext";
import { PLACEHOLDER_SHOP } from "../../../../utils/placeholder";
import axios from "axios";
import Swal from "sweetalert2";

const ShopDetail = () => {
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFiles, selectedShop } = useContext(MyContext);

  // ── Data States ───────────────────────────────────────────────
  const [shopDetail, setShopDetail] = useState(null);
  const [shopProducts, setShopProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  // ── Filter States ─────────────────────────────────────────────
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeSubcategory, setActiveSubcategory] = useState(null);
  const [openCategoryId, setOpenCategoryId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRating, setSelectedRating] = useState("all");
  const [subcategoryDropdownOpen, setSubcategoryDropdownOpen] = useState(false);

  // ── Skeleton ──────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1100);
    return () => clearTimeout(t);
  }, []);

  // ── API: viewShopDetails ──────────────────────────────────────
  const viewShopDetails = () => {
    const formData = new FormData();
    formData.append("id", selectedShop.id);

    axios
      .post(`${baseUrl}/customer/shops/viewShopDetails`, formData)
      .then((response) => {
        console.log(response.data);
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setShopDetail(response.data.shop);
          setShopProducts(response.data.shop.kmproducts || []);
        }
      });
  };

  // ── API: viewAllCategories ────────────────────────────────────
  const viewAllCategories = () => {
    axios
      .get(`${baseUrl}/customer/category/viewAllCategories`)
      .then((response) => {
        console.log(response.data);
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setCategories(response.data.categories);
        }
      });
  };

  // ── API: viewSubcategoriesByCategoryId ────────────────────────
  const viewSubcategoriesByCategoryId = (categoryId) => {
    const formData = new FormData();
    formData.append("kmcategory_id", categoryId);

    axios
      .post(
        `${baseUrl}/customer/subcategory/viewAllSubcategories`,
        formData,
      )
      .then((response) => {
        console.log(response.data);
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setSubcategories(response.data.subcategories);
        }
      });
  };

  useEffect(() => {
    viewShopDetails();
    viewAllCategories();
  }, []);

  // ── Filter Logic ──────────────────────────────────────────────
  const filteredProducts = useMemo(() => {
    return shopProducts.filter((p) => {
      const matchesSearch = p.name
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase());

      let matchesCategory = true;
      if (activeSubcategory) {
        matchesCategory = p.kmsubcategory?.id === activeSubcategory.id;
      } else if (activeCategory) {
        matchesCategory = p.kmsubcategory?.kmcategory?.id === activeCategory.id;
      }

      const matchesRating =
        selectedRating === "all" ||
        (selectedRating === "4+" && p.rating >= 4) ||
        (selectedRating === "4.5+" && p.rating >= 4.5);

      return matchesSearch && matchesCategory && matchesRating;
    });
  }, [
    searchQuery,
    activeCategory,
    activeSubcategory,
    selectedRating,
    shopProducts,
  ]);

  if (loading)
    return (
      <div className="ssd-page">
        <MyNavbar />
        <SingleProductSkeleton />
        <Footer />
      </div>
    );

  return (
    <>
      <div className="ssd-page">
        <MyNavbar />

        {/* ── SHOP HERO ── */}
        <div className="ssd-hero">
          <div
            className="ssd-hero-cover"
            style={{
              backgroundImage: shopDetail?.kmbusinessdetails?.logo
                ? `url(${baseUrlForFiles}/${shopDetail.kmbusinessdetails.logo})`
                : "none",
            }}
          >
            <div className="ssd-hero-overlay" />
          </div>

          <Container className="ssd-hero-content">
            <div className="ssd-hero-inner">
              <div className="ssd-logo-wrapper">
                <img
                  src={
                    shopDetail?.kmbusinessdetails?.logo
                      ? `${baseUrlForFiles}/${shopDetail.kmbusinessdetails.logo}`
                      : PLACEHOLDER_SHOP
                  }
                  alt={shopDetail?.shop_name}
                  className="ssd-logo"
                  onError={(e) => {
                    e.currentTarget.onerror = null; // loop band
                    e.currentTarget.src = PLACEHOLDER_SHOP;
                  }}
                />
                {shopDetail?.verified && (
                  <span className="ssd-verified-badge">
                    <FaCheckCircle />
                  </span>
                )}
              </div>

              <div className="ssd-hero-info">
                <h1 className="ssd-shop-name">{shopDetail?.shop_name}</h1>
                <p className="ssd-tagline">
                  {shopDetail?.kmbusinessdetails?.tagline}
                </p>

                <div className="ssd-stats-row">
                  <div className="ssd-stat">
                    <div className="ssd-stars">
                      {[...Array(5)].map((_, i) => (
                        <FaStar
                          key={i}
                          className={
                            i < Math.floor(shopDetail?.rating || 0)
                              ? "ssd-star-filled"
                              : "ssd-star-empty"
                          }
                        />
                      ))}
                    </div>
                  </div>
                  <div className="ssd-stat">
                    <FaMapMarkerAlt className="ssd-stat-icon" />
                    <span className="ssd-stat-text">
                      {shopDetail?.kmbusinessdetails?.address ||
                        shopDetail?.location}
                    </span>
                  </div>
                  <div className="ssd-stat">
                    <FaBox className="ssd-stat-icon" />
                    <span className="ssd-stat-text">
                      {shopProducts.length} Products
                    </span>
                  </div>
                </div>

                <p className="ssd-description">
                  {shopDetail?.kmbusinessdetails?.description}
                </p>
              </div>
            </div>
          </Container>
        </div>

        {/* ── TOPBAR FILTERS ── */}
        <div className="ssd-filterbar">
          <Container>
            <div className="ssd-filterbar-inner">
              {/* All button */}
              <button
                className={`ssd-cat-btn ${!activeCategory ? "active" : ""}`}
                onClick={() => {
                  setActiveCategory(null);
                  setActiveSubcategory(null);
                  setOpenCategoryId(null);
                  setSubcategories([]);
                  setSubcategoryDropdownOpen(false);
                }}
              >
                <FaStore className="ssd-cat-btn-icon" />
                All
              </button>

              {/* Category Buttons — backend se */}
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  className={`ssd-cat-btn ${activeCategory?.id === cat.id ? "active" : ""}`}
                  onClick={() => {
                    setActiveCategory(cat);
                    setActiveSubcategory(null);
                    setOpenCategoryId(cat.id);
                    setSubcategoryDropdownOpen(true);
                    viewSubcategoriesByCategoryId(cat.id);
                  }}
                >
                  <FaBox className="ssd-cat-btn-icon" />
                  {cat.name}
                </button>
              ))}

              <div className="ssd-filterbar-divider" />

              {/* Subcategory Dropdown — backend se */}
              {subcategories.length > 0 && (
                <div className="ssd-filter-group ssd-subcategory-group">
                  <div className="ssd-dropdown-wrapper">
                    <button
                      className={`ssd-dropdown-btn ${activeSubcategory ? "active" : ""}`}
                      onClick={() => setSubcategoryDropdownOpen((p) => !p)}
                    >
                      <FaFilter className="ssd-dropdown-icon" />
                      <span>{activeSubcategory?.subcategoryname || "Subcategory"}</span>
                      <FaChevronDown
                        className={`ssd-chevron ${subcategoryDropdownOpen ? "open" : ""}`}
                      />
                    </button>
                    {subcategoryDropdownOpen && (
                      <div className="ssd-dropdown-menu">
                        {subcategories.map((sub) => (
                          <button
                            key={sub.id}
                            className={`ssd-dropdown-item ${activeSubcategory?.id === sub.id ? "active" : ""}`}
                            onClick={() => {
                              setActiveSubcategory(sub);
                              setSubcategoryDropdownOpen(false);
                            }}
                          >
                            <FaLeaf className="ssd-dropdown-item-icon" />
                            {sub.subcategoryname}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Search */}
              <div className="ssd-filter-group ssd-search-group">
                <div className="ssd-search-box">
                  <FaSearch className="ssd-search-icon" />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="ssd-search-input"
                  />
                </div>
              </div>

              {/* Rating */}
              <div className="ssd-filter-group">
                <select
                  className={`ssd-rating-select ${selectedRating !== "all" ? "active" : ""}`}
                  value={selectedRating}
                  onChange={(e) => setSelectedRating(e.target.value)}
                >
                  <option value="all">All Ratings</option>
                  <option value="4+">4+ Stars</option>
                  <option value="4.5+">4.5+ Stars</option>
                </select>
              </div>

              {/* Clear */}
              {(activeSubcategory ||
                searchQuery ||
                selectedRating !== "all" ||
                activeCategory) && (
                <button
                  className="ssd-clear-btn"
                  onClick={() => {
                    setActiveCategory(null);
                    setActiveSubcategory(null);
                    setOpenCategoryId(null);
                    setSubcategories([]);
                    setSearchQuery("");
                    setSelectedRating("all");
                    setSubcategoryDropdownOpen(false);
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          </Container>
        </div>

        {/* ── PRODUCTS SECTION ── */}
        <section className="ssd-products-section">
          <Container>
            <div className="ssd-products-header">
              <h2 className="ssd-products-title">Our Products</h2>
              <p className="ssd-results-count">
                <span className="ssd-results-num">
                  {filteredProducts.length}
                </span>{" "}
                product{filteredProducts.length !== 1 ? "s" : ""} found
                {activeSubcategory && (
                  <span className="ssd-active-filter">
                    {" "}
                    in "{activeSubcategory.subcategoryname}"
                  </span>
                )}
                {activeCategory && !activeSubcategory && (
                  <span className="ssd-active-filter">
                    {" "}
                    in "{activeCategory.categoryname}"
                  </span>
                )}
              </p>
            </div>

            {filteredProducts.length > 0 ? (
              <Row className="g-4">
                {filteredProducts.map((product, index) => (
                  <Col key={product.id} xs={12} sm={6} lg={3}>
                    <ProductCard
                      product={product}
                      index={index}
                      baseUrlForFiles={baseUrlForFiles}
                    />
                  </Col>
                ))}
              </Row>
            ) : (
              <div className="ssd-no-results">
                <FaFilter className="ssd-no-results-icon" />
                <h4>No products found</h4>
                <p>Try a different category or clear your filters</p>
                <button
                  className="ssd-no-results-btn"
                  onClick={() => {
                    setActiveCategory(null);
                    setActiveSubcategory(null);
                    setOpenCategoryId(null);
                    setSubcategories([]);
                    setSearchQuery("");
                    setSelectedRating("all");
                    setSubcategoryDropdownOpen(false);
                  }}
                >
                  Clear Filters
                </button>
              </div>
            )}
          </Container>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default ShopDetail;
